/**
 * The shape the shell hands to both nav surfaces.
 *
 * The active-state styling is NOT written here any more. It used to be — accent
 * text plus a slim side indicator and explicitly no fill — while the seller
 * portal drew a filled neutral pill for the same state. Two shells, two
 * answers, one product: exactly the divergence the platform rule exists to
 * stop. `navRowClass` now lives in `@a2z/ui` and both apps read it; see the
 * note there for why the seller's treatment is the one that won.
 */
export { navRowClass } from "@a2z/ui";

import type { NavIconKey } from "./navIcons";
import type { ManageRowKey } from "./rows";

/**
 * SIX groups, by JOB, since the 2026-10-14 round table (D152, T8): الصالة
 * (`service`) · الفلوس (`money`) · المنيو · المخزن (`inventory`) · التقارير ·
 * المحل (`store`). The keys of the five older groups are kept so every caller
 * and message key still reads; what changed is which rows sit where, and the
 * Arabic heading of three of them.
 */
export type ManageNavGroup =
  | "service"
  | "money"
  | "menu"
  | "inventory"
  | "store"
  | "reports";

export type ManageNavItem = {
  /** The row this item renders — `rows.ts`'s key, which is also its message key. */
  rowKey: ManageRowKey;
  to: string;
  label: string;
  /** Which glyph the row carries. See `navIcons.tsx` for why every row has one. */
  icon: NavIconKey;
  /**
   * Which labelled section the row sits under.
   *
   * The seller portal groups its sidebar (المتجر / الكتالوج / قنوات البيع) and
   * this app did not, which left a flat run of nine rows where the platform
   * shows three short scannable blocks. Grouping also fixes the ordering: the
   * old list interleaved service, menu and shop concerns.
   */
  group: ManageNavGroup;
};

/**
 * Should this row light up ONLY on its own URL, rather than on everything
 * beneath it?
 *
 * React Router's `NavLink` matches by PREFIX unless told otherwise, which is
 * right for a row whose detail pages are not in the nav — open a dish and
 * `المنيو` should stay lit. It is wrong when the nav ALSO carries the children:
 * `manage/reports` then stayed lit on all five of its report pages, so the
 * sidebar showed two active rows at once and neither told you where you were.
 *
 * Derived from the nav rather than flagged per row, so the answer cannot rot:
 * a row is exact exactly when another row sits beneath it, and adding or
 * removing a sub-page changes the behaviour with it. The `/` matters — it is
 * what keeps `manage/reports` from claiming a hypothetical
 * `manage/reports-archive`.
 */
export function matchesExactly(
  to: string,
  items: readonly Pick<ManageNavItem, "to">[],
): boolean {
  return items.some((other) => other.to !== to && other.to.startsWith(`${to}/`));
}
